"use strict";
// Copyright © Veeam Software Group GmbH
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NavigationRouter = void 0;
const routr_1 = __importDefault(require("routr"));
const rxjs_1 = require("rxjs");
const helpers_1 = require("./helpers");
const RouterApi_1 = require("./RouterApi");
const RouterMatchResult_1 = require("./RouterMatchResult");
class NavigationRouter {
    constructor(config, history, getContext, useHashbang = true) {
        this.history = history;
        this.getContext = getContext;
        this.useHashbang = useHashbang;
        this.removeHistoryListener = null;
        this.subject = new rxjs_1.Subject();
        this.match = (url) => {
            const urlDoc = document.createElement('a');
            urlDoc.href = url;
            const { pathname, search, hash } = urlDoc;
            const parsedUrl = `/${pathname}${search}${hash}`.replace(/\/+/g, '/');
            const routeUrl = this.useHashbang ? parsedUrl.replace(/[\w/-]+#\//, '/') : parsedUrl;
            const result = this.routr.getRoute(routeUrl);
            if (!result) {
                return this.noMatchResult;
            }
            return new RouterMatchResult_1.RouterMatchResult({
                name: result.name || '',
                path: result.path || '',
                parents: result.config.parents || [],
                redirect: result.config.redirect || '',
                resourceName: result.config.resourceName || null,
                guards: result.config.guards
            }, {
                params: result.params || {},
                query: result.query
            }, this.getContext);
        };
        this.resolvePath = (name, params, query) => {
            const path = this.routr.makePath(name, params || {}, query);
            if (!path) {
                throw new Error(`Router error: can't resolve path for "${name}" name`);
            }
            const result = path.replace(/\/+$/, '').replace(/\/+\?/, '?') || '/';
            return `${this.useHashbang ? '/#' : ''}${result}`;
        };
        this.resolveRoute = (url) => {
            const matchResult = this.match(url);
            return {
                name: matchResult.getName(),
                params: matchResult.getParams(),
                query: matchResult.getQuery()
            };
        };
        this.block = (prompt) => {
            if (this.unblockCallback) {
                throw new Error('Router error: a router supports only one block at a time');
            }
            this.unblockCallback = this.history.block(prompt);
        };
        this.unblock = () => {
            var _a;
            (_a = this.unblockCallback) === null || _a === void 0 ? void 0 : _a.call(this);
            this.unblockCallback = undefined;
        };
        this.isBlocked = () => {
            return !!this.unblockCallback;
        };
        this.config = (0, helpers_1.parseConfig)(config.route);
        this.routr = new routr_1.default(this.config.map((item) => ({
            name: item.name,
            path: item.path,
            method: 'get',
            parents: item.parents,
            redirect: item.redirect,
            resourceName: item.resourceName,
            guards: item.guards
        })));
        this.currentMatch = null;
        this.noMatchResult = new RouterMatchResult_1.RouterMatchResult({
            name: config.noMatchName || '',
            path: '',
            guards: [],
            parents: []
        }, null, this.getContext);
        if (config.noMatchRedirect && config.noMatchRedirect.name) {
            this.noMatchResult = this.match(this.resolvePath(config.noMatchRedirect.name));
        }
    }
    updateCurrentMatch(matchResult) {
        if (matchResult.isDisallow()) {
            const { redirect, stubName } = matchResult.getGuardResult();
            if (redirect && redirect.name) {
                const redirectUrl = this.resolvePath(redirect.name);
                this.redirect(this.match(redirectUrl));
                return;
            }
            if (!stubName) {
                this.updateCurrentMatch(this.noMatchResult);
                return;
            }
        }
        if (matchResult.hasRedirect()) {
            this.redirect(matchResult);
            return;
        }
        this.currentMatch = matchResult;
        this.subject.next(matchResult);
    }
    redirect(matchResult) {
        var _a;
        let redirectName = '';
        try {
            redirectName = (_a = matchResult.getRedirect) === null || _a === void 0 ? void 0 : _a.call(matchResult).name;
        }
        catch (_b) {
            redirectName = matchResult.getName();
        }
        const redirectUrl = this.resolvePath(redirectName, matchResult.getParams(), matchResult.getQuery());
        const newMatchResult = this.match(redirectUrl);
        this.history.replace(redirectUrl);
        this.updateCurrentMatch(newMatchResult);
    }
    run(url) {
        this.updateCurrentMatch(this.match(url));
        if (this.removeHistoryListener) {
            throw new Error('Router error: before run router again you must all "terminate" method');
        }
        this.removeHistoryListener = this.history.listen(({ pathname, search, hash }) => {
            const newUrl = `${pathname}${search}${hash}`;
            this.updateCurrentMatch(this.match(newUrl));
        });
    }
    terminate() {
        if (this.removeHistoryListener) {
            this.removeHistoryListener();
            this.removeHistoryListener = null;
        }
        this.currentMatch = null;
    }
    isActive() {
        return !!this.removeHistoryListener;
    }
    asObservable() {
        return this.subject.asObservable();
    }
    getCurrentMatch() {
        if (!this.currentMatch) {
            throw new Error('Router error: before call getCurrentMatch you must call run');
        }
        return this.currentMatch;
    }
    isRouteAllow(routeName, params, query) {
        return this.match(this.resolvePath(routeName, params, query)).isAllow();
    }
    getActions() {
        return new RouterApi_1.RouterApi(this, this.history);
    }
}
exports.NavigationRouter = NavigationRouter;
//# sourceMappingURL=NavigationRouter.js.map